import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Modal, LinearProgress, Button } from '@material-ui/core';
import styles from './AsnSelectorDisplay.module.css';
import { ASN_STATUS, DATE_FORMAT, AGGREGATE_STATUS } from '../../constants';

import { getDatesForRange } from '../../utils/utils';
import '../../cssModules/switch.css';
import TodayIcon from '@material-ui/icons/Today';
import AttributesSelector from '../AttributesSelector/AttributesSelector';
import { optionalColumnsWithShift } from '../../staticData';
import { getSelectedOptionalColumns } from '../../utils/tableUtils';
import VirtualizedDragAndDropList from '../VirtualizedDragAndDropList/VirtualizedDragAndDropList';

const AsnSelectorDisplay = ({
  asnToLineOn,
  handleClose,
  open,
  updateRows,
  asnData,
  asnSelectorheaderText,
  setAsnData,
  lastAsnOrAgsnGeneratedDate,
  handleLineOnSubmit,
  setActiveDate,
  activeDate,
  fixedColumns,
  selectedLine,
}) => {
  const [asnDates, setAsnDates] = useState([]);

  const [selectedOptionalColNames, setSelectedOptionalColNames] = useState([]);

  const [
    indexOfLastAnsNotInPlannedState,
    setIndexOfLastAnsNotInPlannedState,
  ] = useState(-1);

  const insertAsnToLineOnBeforeFirstPlannedAsn = asns => {
    const rows = asns.filter(
      res =>
        res.asnId !== (asnToLineOn && asnToLineOn.asnId) ||
        res.agsnId !== (asnToLineOn && asnToLineOn.agsnId),
    );
    let firstPlannedAsnIndex = rows.findIndex(
      asn =>
        asn.status === ASN_STATUS.PLANNED ||
        asn.status === AGGREGATE_STATUS.ASN_GENERATED,
    );

    let selectionInsertedRows;

    if (firstPlannedAsnIndex === -1) {
      selectionInsertedRows = [
        ...rows,
        { ...asnToLineOn, isToBeLinedOn: true },
      ];
    } else {
      selectionInsertedRows = [
        ...rows.slice(0, firstPlannedAsnIndex),
        { ...asnToLineOn, isToBeLinedOn: true },
        ...rows.slice(firstPlannedAsnIndex),
      ];
    }
    setAsnData(prevState => ({ ...prevState, data: selectionInsertedRows }));
  };

  const loadIndexOfLastAnsNotInPlannedState = asns => {
    const asnsNotInPlanningState = asns.filter(
      asn =>
        asn.status !== ASN_STATUS.PLANNED &&
        asn.status !== AGGREGATE_STATUS.ASN_GENERATED,
    );
    const lastAsnNotInPlannedState =
      asnsNotInPlanningState[asnsNotInPlanningState.length - 1];
    setIndexOfLastAnsNotInPlannedState(asns.indexOf(lastAsnNotInPlannedState));
  };

  const fixedColumnsStyles = {
    headerStyles: {
      color: '#0275A7',
      fontSize: '13px',
      fontWeight: '600',
      boxSizing: 'border-box',
    },
  };

  const fixedColumnsStyled = fixedColumns.map(column => ({
    ...column,
    ...fixedColumnsStyles,
  }));

  const optionalColumnsStyles = {
    headerStyles: {
      color: '#707070',
      backgroundColor: 'white',
      zIndex: 1,
      boxSizing: 'border-box',
    },
  };

  const selectedOptionalColumns = getSelectedOptionalColumns(
    optionalColumnsWithShift,
    selectedOptionalColNames,
    optionalColumnsStyles,
  );

  const attributesTableColumns = [
    ...fixedColumnsStyled,
    ...selectedOptionalColumns,
  ];

  const handleDragEnd = result => {
    if (!result.destination) {
      return;
    }
    if (
      result.source.index === result.destination.index ||
      result.destination.index <= indexOfLastAnsNotInPlannedState
    ) {
      return;
    }

    const rows = asnData.data;
    const [removed] = rows.splice(result.source.index, 1);
    rows.splice(result.destination.index, 0, removed);
    setAsnData(prevState => ({ ...prevState, data: rows }));
  };

  const renderTableView = () => {
    return (
      <>
        <div className={styles.header}>
          <div className={styles.lineName}>{selectedLine}</div>
          <div className={styles.title}>
            <span style={{ display: 'block' }}>Drop Sequence</span>
            <span style={{ display: 'block', fontWeight: '600' }}>
              Select Slot
            </span>
          </div>
          <div className={styles.tableTop}>
            <div className={styles.calicon}>
              <span>For</span>
              <TodayIcon />
            </div>
            <div className={styles.datesSelectorBar}>
              <span className={styles.monthName}>
                {moment(activeDate, DATE_FORMAT.ISO).format('MMMM')}
              </span>
              <div className={styles.hrScroll}>
                <div className={styles.datesRow}>
                  {asnDates.map(date => (
                    <button
                      className={
                        date.format(DATE_FORMAT.ISO) ===
                          activeDate.format(DATE_FORMAT.ISO)
                          ? styles.activeDateButton
                          : styles.datesSelectorButton
                      }
                      onClick={() => setActiveDate(date)}
                      key={date}
                    >
                      {moment(date, DATE_FORMAT.ISO).format('DD')}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <span className={styles.dragAndDropNote}>
              {asnSelectorheaderText}
            </span>
            <div className={styles.ToolBar}>
              <AttributesSelector
                className={styles.Attribute}
                columns={optionalColumnsWithShift}
                selectedOptionalCols={selectedOptionalColNames}
                applyColumnsSelection={selection =>
                  setSelectedOptionalColNames(selection)
                }
                isShiftPresent={true}
              />
            </div>
          </div>
        </div>

        <div className={styles.mainTableContainer} style={{ height: 400 }}>
          <div className={styles.tableContainer}>
            <>
              <div className={styles.tableHeight} style={{ height: 370 }}>
                <VirtualizedDragAndDropList
                  columns={attributesTableColumns}
                  rows={asnData.data || []}
                  listHeight={370}
                  rowHeight={40}
                  classNames={{
                    tableRowClassName: row => {
                      if (row.isToBeLinedOn) return styles.lineOnRow;
                      if (
                        row.status !== ASN_STATUS.PLANNED &&
                        row.status !== AGGREGATE_STATUS.ASN_GENERATED
                      )
                        return styles.greyedOutRow;
                      return styles.defautRow;
                    },
                  }}
                  shouldDisableDragForRow={row =>
                    ASN_STATUS.LINE_OUT !== row.status &&
                    AGGREGATE_STATUS.LINED_OUT !== row.status
                  }
                  shouldFixRow={row =>
                    row.status !== ASN_STATUS.PLANNED &&
                    row.status !== ASN_STATUS.LINE_OUT &&
                    row.status !== AGGREGATE_STATUS.ASN_GENERATED &&
                    row.status !== AGGREGATE_STATUS.LINED_OUT
                  }
                  onDragEnd={handleDragEnd}
                />
              </div>
            </>
          </div>
        </div>

        <div className={styles.footer}>
          <Button
            data-testid="asn-select-confirm"
            className={styles.footerButton}
            variant="contained"
            onClick={() => {
              handleLineOnSubmit(asnData, activeDate);
              handleClose();
            }}
          >
            Confirm
          </Button>
        </div>
      </>
    );
  };

  useEffect(() => {
    const dates = getDatesForRange(moment(), lastAsnOrAgsnGeneratedDate);
    setAsnDates(dates);
    setActiveDate(dates[0]);
  }, [lastAsnOrAgsnGeneratedDate, setActiveDate]);
  useEffect(() => {
    if (!asnData.loading) {
      loadIndexOfLastAnsNotInPlannedState(asnData.data);
      insertAsnToLineOnBeforeFirstPlannedAsn(asnData.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asnData.loading]);
  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="heijunka-sequence-modal"
      aria-describedby="modal to show heijunka sequence"
    >
      <div className={styles.container}>
        {asnData.loading ? <LinearProgress /> : renderTableView()}
      </div>
    </Modal>
  );
};

AsnSelectorDisplay.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  updateRows: PropTypes.func.isRequired,
  asnToLineOn: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  activeDate: PropTypes.objectOf(moment),
  setActiveDate: PropTypes.func.isRequired,
  asnData: PropTypes.arrayOf(
    PropTypes.shape({
      isLoading: PropTypes.bool.isRequired,
      data: PropTypes.array.isRequired,
      error: PropTypes.string,
    }),
  ),
  asnSelectorheaderText: PropTypes.string.isRequired,
  setAsnData: PropTypes.func.isRequired,
  lastAsnOrAgsnGeneratedDate: PropTypes.objectOf(moment),
  handleLineOnSubmit: PropTypes.func.isRequired,
  fixedColumns: PropTypes.array.isRequired,
  selectedLine: PropTypes.string.isRequired,
};

export default AsnSelectorDisplay;
